import { useIntegrationApp } from '@integration-app/react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Badge, BadgeVariant } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';

export type IntegrationStatus = 'available' | 'unavailable';
export type ConnectionStatus = 'connected' | 'disconnected';

export type IntegrationCardProps = {
  integrationId: string;
  image: string;
  title: string;
  integrationStatus: IntegrationStatus;
  connectionStatus: ConnectionStatus;
  buttonText?: string;
  onButtonClick?: () => Promise<void>;
};

export const IntegrationCard = ({
  integrationId,
  image,
  title,
  integrationStatus,
  connectionStatus,
  onButtonClick,
}: IntegrationCardProps) => {
  const navigate = useNavigate();
  const integrationApp = useIntegrationApp();
  const isConnected = connectionStatus === 'connected';
  const isUnavailable = integrationStatus === 'unavailable';
  const badgeVariant: BadgeVariant = isConnected ? 'success' : 'secondary';
  let buttonText = 'Connect';
  if (isConnected) {
    buttonText = 'Disconnect';
  }
  if (isUnavailable) {
    buttonText = 'Coming Soon';
  }

  const onTitleClick = () => {
    navigate(`/data-mapping/${integrationId}`);
  };
  const onIntegrationClick = async (integrationId: string) => {
    if (isConnected) {
      await integrationApp?.integration(integrationId).disconnect();
      await onButtonClick?.();
    } else {
      await integrationApp
        ?.integration(integrationId)
        .open({ onClose: onButtonClick });
    }
  };

  return (
    <Card className="flex h-48 w-full flex-col justify-between">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src={image}
            alt={title}
            className="h-10 w-10 flex-shrink-0 object-contain"
          />
          <div
            className={`text-lg font-medium ${isConnected ? 'cursor-pointer hover:underline' : ''}`}
            onClick={isConnected ? onTitleClick : undefined}
          >
            {title}
          </div>
        </div>
        <Badge variant={badgeVariant} className="flex items-center gap-2">
          {isConnected ? <CheckCircle size={20} /> : <XCircle size={20} />}
        </Badge>
      </div>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          onIntegrationClick(integrationId);
        }}
        className="w-full"
        disabled={isUnavailable}
      >
        {buttonText}
      </Button>
    </Card>
  );
};
